import { FormEvent, useEffect, useState } from 'react';
import { X } from 'lucide-react';

import { useAdminCreateCompanyTeamMutation } from '../../hooks/useApi';
import { getMutationErrorMessage } from '../../lib/apiErrors';
import { Button } from '../ui/button';
import { formFieldGroupSurfaceClassName } from '../ui/formPrimitives';
import { Input } from '../ui/input';

interface AdminCreateTeamModalProps {
    open: boolean;
    companyId: number;
    onClose: () => void;
    onCreated?: () => void;
}

export function AdminCreateTeamModal({
    open,
    companyId,
    onClose,
    onCreated,
}: AdminCreateTeamModalProps) {
    const createTeam = useAdminCreateCompanyTeamMutation(companyId);
    const [name, setName] = useState('');
    const [isActive, setIsActive] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!open) return;
        setName('');
        setIsActive(true);
        setError(null);
    }, [open]);

    if (!open) return null;

    const handleSubmit = async (event: FormEvent) => {
        event.preventDefault();
        setError(null);

        const trimmed = name.trim();
        if (!trimmed) {
            setError('Team name is required.');
            return;
        }

        try {
            await createTeam.mutateAsync({ name: trimmed, isActive });
            onCreated?.();
            onClose();
        } catch (err) {
            setError(getMutationErrorMessage(err, 'Failed to create team.'));
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
            <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-xl bg-white shadow-xl">
                <div className="flex items-start justify-between gap-4 border-b p-5">
                    <h2 className="text-xl font-semibold text-neutral-950">Create team</h2>
                    <button
                        type="button"
                        onClick={onClose}
                        className="rounded-full p-1 text-neutral-400 hover:bg-neutral-100 hover:text-neutral-700"
                    >
                        <X className="size-5" />
                    </button>
                </div>

                <form className="space-y-5 p-5" onSubmit={handleSubmit}>
                    {error && (
                        <p className="rounded-md bg-red-50 p-3 text-sm text-red-700">{error}</p>
                    )}

                    <label className="block space-y-1">
                        <span className="text-sm font-medium text-neutral-800">Name</span>
                        <Input
                            value={name}
                            onChange={(event) => setName(event.target.value)}
                            placeholder="Team name"
                            autoComplete="off"
                        />
                    </label>

                    <label className={`flex items-center gap-3 ${formFieldGroupSurfaceClassName}`}>
                        <input
                            type="checkbox"
                            checked={isActive}
                            onChange={(event) => setIsActive(event.target.checked)}
                            className="size-4 rounded border-neutral-300 text-[#276db0] focus:ring-[#276db0]"
                        />
                        <span className="text-sm font-medium text-neutral-950">Active</span>
                    </label>

                    <div className="flex justify-end gap-2 border-t pt-4">
                        <Button type="button" variant="outline" onClick={onClose}>
                            Cancel
                        </Button>
                        <Button type="submit" disabled={createTeam.isPending}>
                            {createTeam.isPending ? 'Creating…' : 'Create team'}
                        </Button>
                    </div>
                </form>
            </div>
        </div>
    );
}
